// "What's new" (VRG-221) react-query hooks.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { announcementsApi } from 'services'
import type { AnnouncementWritePayload } from 'services/announcementsApi'

const FEED_KEY = ['announcements', 'feed']
const INTERNAL_KEY = ['internal', 'announcements']

/**
 * The company-facing feed. Read by BOTH the once-per-session modal and the
 * `/whats-new` page, and by the sidebar Help row's "New" marker.
 *
 * One query key for all three, which is the point: they must agree. If the modal
 * fetched separately from the page, dismissing in the modal would leave the page
 * still showing it as unseen until its own refetch — and the Help row's dot would
 * disagree with both.
 *
 * `staleTime` is 2 minutes. Announcements are published by hand, a handful of times
 * a month; refetching on every mount would be one request per navigation to learn
 * nothing. It is not `Infinity` because the "New" marker should clear across tabs
 * within a session without a reload.
 */
export const useAnnouncementFeed = () =>
  useQuery({
    queryKey: FEED_KEY,
    queryFn: () => announcementsApi.feed(),
    staleTime: 2 * 60 * 1000,
    retry: false,
  })

/**
 * ONE announcement, for the `/whats-new/:id` detail page.
 *
 * ## Its own query key, not a lookup in the feed's cache
 *
 * `select`ing the row out of `FEED_KEY` would be free and would be wrong: the feed
 * omits everything published before the reader signed up, so the announcement most
 * likely to arrive as a link from a colleague is exactly the one that is not in it.
 * A separate key backed by `GET /announcements/:id` (which drops the signup cutoff but
 * keeps the audience gate) is the only version that resolves an old link correctly.
 *
 * `staleTime` matches the feed's 2 minutes for the same reason: an announcement is
 * immutable in practice once published, and re-fetching it on every mount would be a
 * request per navigation to learn nothing. `retry: false` because the expected failures
 * here are 404 and 403, and retrying either three times only delays the state that
 * tells the reader what happened.
 */
export const useAnnouncement = (id: string | undefined) =>
  useQuery({
    queryKey: [...FEED_KEY, 'detail', id],
    // Non-null assertion is safe under `enabled` — react-query never calls the fn
    // while it is false.
    queryFn: () => announcementsApi.get(id!),
    enabled: Boolean(id),
    staleTime: 2 * 60 * 1000,
    retry: false,
  })

/**
 * Dismiss one announcement for this user, permanently.
 *
 * Invalidates the feed so the page's card loses its unseen treatment and the Help
 * row's dot clears — without a reload, and in whichever surface was not the one
 * that dismissed it.
 */
export const useMarkAnnouncementSeen = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => announcementsApi.markSeen(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FEED_KEY })
    },
  })
}

// ── Internal (staff) ────────────────────────────────────────────────────────

export const useInternalAnnouncements = () =>
  useQuery({
    queryKey: INTERNAL_KEY,
    queryFn: () => announcementsApi.internalList(),
    // No retry: the expected failure here is a 403 (the permission is ungranted
    // until the role-grant question is answered), and retrying a 403 three times
    // just delays the error state by a second and a half.
    retry: false,
  })

/**
 * ONE announcement, drafts included — what `/internal/announcements/:id/edit` loads.
 *
 * Its own key rather than a `select` over `INTERNAL_KEY`, because the edit page has to
 * work as a cold entry point: a pasted URL or a reload mounts the form with the list
 * query never having run, so a cache lookup would resolve to `undefined` and the form
 * would render blank over a real record.
 *
 * `retry: false` matches every other internal query here — the expected failure is a
 * 403 or a 404, and retrying either only delays the state that explains it.
 */
export const useInternalAnnouncement = (id: string | undefined) =>
  useQuery({
    queryKey: [...INTERNAL_KEY, 'detail', id],
    queryFn: () => announcementsApi.internalGet(id!),
    enabled: Boolean(id),
    retry: false,
  })

export const useInternalAnnouncementBoardItems = () =>
  useQuery({
    queryKey: [...INTERNAL_KEY, 'board-items'],
    queryFn: () => announcementsApi.internalBoardItems(),
    retry: false,
  })

export const useCreateAnnouncement = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: AnnouncementWritePayload) => announcementsApi.internalCreate(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INTERNAL_KEY })
    },
  })
}

export const useUpdateAnnouncement = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...payload }: AnnouncementWritePayload & { id: string }) =>
      announcementsApi.internalUpdate(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INTERNAL_KEY })
    },
  })
}

export const useSetAnnouncementPublished = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, published }: { id: string; published: boolean }) =>
      announcementsApi.internalSetPublished(id, published),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INTERNAL_KEY })
      // Publishing is exactly what changes the COMPANY feed, so that cache has to
      // go too — otherwise a staffer publishes, switches to the company surface in
      // the same session, and sees nothing.
      queryClient.invalidateQueries({ queryKey: FEED_KEY })
    },
  })
}

export const useNotifyRequesters = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => announcementsApi.internalNotifyRequesters(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INTERNAL_KEY })
      // The fan-out flips linked board items to `shipped`, so any board or support
      // list already in cache is now stale.
      queryClient.invalidateQueries({ queryKey: ['internal', 'support'] })
      queryClient.invalidateQueries({ queryKey: ['support'] })
    },
  })
}

export const useDeleteAnnouncement = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => announcementsApi.internalRemove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INTERNAL_KEY })
      queryClient.invalidateQueries({ queryKey: FEED_KEY })
    },
  })
}
